// components/AccessListHistory.js - Access list change history view
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, CalendarClock, Filter, BarChart3, Copy, Check, ChevronDown, ChevronRight, X, List } from 'lucide-react';
import { backfillAccessListHistory, fetchAccessListHistory } from '../services/api/accessListApi';

const RANGE_OPTIONS = [
    { value: '24h', label: 'Last 24h', ms: 24 * 60 * 60 * 1000 },
    { value: '7d',  label: 'Last 7d',  ms: 7  * 24 * 60 * 60 * 1000 },
    { value: '30d', label: 'Last 30d', ms: 30 * 24 * 60 * 60 * 1000 },
];
const BUCKET_OPTIONS = [{ value: 'hour', label: 'Hour' }, { value: 'day', label: 'Day' }, { value: 'week', label: 'Week' }];
const CHANGE_OPTIONS = [{ value: 'all', label: 'All Types' }, { value: 'new', label: 'New' }, { value: 'missing', label: 'Missing' }, { value: 'changed', label: 'Changed' }];

/* ── copy helper with HTTP fallback ── */
function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text);
    }
    return new Promise((resolve, reject) => {
        const el = document.createElement('textarea');
        el.value = text;
        el.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0';
        document.body.appendChild(el);
        el.focus(); el.select();
        try {
            const ok = document.execCommand('copy');
            document.body.removeChild(el);
            ok ? resolve() : reject(new Error('execCommand failed'));
        } catch (err) { document.body.removeChild(el); reject(err); }
    });
}

/* ── CopyButton ── */
const CopyButton = ({ text, size = 11 }) => {
    const [state, setState] = useState('idle');
    const handle = (e) => {
        e.stopPropagation();
        copyText(String(text))
            .then(() => { setState('ok');  setTimeout(() => setState('idle'), 1500); })
            .catch(()  => { setState('err'); setTimeout(() => setState('idle'), 1500); });
    };
    return (
        <button className="copy-btn" onClick={handle} title={`Copy "${text}"`}>
            {state === 'ok'   && <Check size={size} style={{ color: '#10b981' }} />}
            {state === 'err'  && <X     size={size} style={{ color: '#f87171' }} />}
            {state === 'idle' && <Copy  size={size} />}
        </button>
    );
};

/* ── RangesPopup ── */
const RangesPopup = ({ popup, onClose }) => {
    const overlayRef = useRef(null);
    useEffect(() => {
        const h = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', h);
        return () => document.removeEventListener('keydown', h);
    }, [onClose]);
    if (!popup) return null;

    const { destination, ranges, kind } = popup;
    const accent = kind === 'added' ? '#10b981' : '#f97316';
    const accentBg = kind === 'added' ? 'rgba(16,185,129,0.12)' : 'rgba(249,115,22,0.12)';
    const allText = ranges.map((r) => (typeof r === 'string' ? r : (r.range || r.cidr || JSON.stringify(r)))).join('\n');

    return (
        <div className="rpm-overlay" ref={overlayRef} onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}>
            <div className="rpm-modal">
                <div className="rpm-header" style={{ borderBottomColor: accent + '33' }}>
                    <div className="rpm-header-left">
                        <List size={13} style={{ color: accent, flexShrink: 0 }} />
                        <span className="rpm-dest mono" style={{ color: accent }}>{destination}</span>
                        <span className="rpm-badge" style={{ background: accentBg, color: accent }}>
                            Ranges {kind === 'added' ? 'Added' : 'Removed'}
                        </span>
                    </div>
                    <div className="rpm-header-right">
                        <span className="rpm-count">{ranges.length} range{ranges.length !== 1 ? 's' : ''}</span>
                        <button className="rpm-close" onClick={onClose}><X size={13} /></button>
                    </div>
                </div>
                <div className="rpm-body">
                    {ranges.length === 0
                        ? <div className="rpm-empty">No ranges recorded.</div>
                        : ranges.map((r, i) => {
                            const rs = typeof r === 'string' ? r : (r.range || r.cidr || JSON.stringify(r));
                            return (
                                <div key={i} className="rpm-row">
                                    <span className="rpm-idx">{i + 1}</span>
                                    <span className="rpm-range mono" style={{ color: accent }}>{rs}</span>
                                    <CopyButton text={rs} size={10} />
                                </div>
                            );
                        })
                    }
                </div>
                {ranges.length > 0 && (
                    <div className="rpm-footer">
                        <CopyButton text={allText} size={11} />
                        <span className="rpm-copy-label">Copy all ranges</span>
                    </div>
                )}
            </div>
        </div>
    );
};

/* ── EventCard ── */
const EventCard = ({ event, isOpen, onToggle, onDestClick, normalizeArray, fmtDate, fmtTime, fmtDelta }) => {
    const destAdded   = normalizeArray(event.destinations_added);
    const destRemoved = normalizeArray(event.destinations_removed);
    const rngAdded    = normalizeArray(event.ranges_added);
    const rngRemoved  = normalizeArray(event.ranges_removed);
    const destChanged = normalizeArray(event.destinations_changed);
    const limitChanges = normalizeArray(event.limit_changes);

    const typeColor = { new: '#10b981', missing: '#f97316', changed: '#38bdf8' }[event.change_type] || '#64748b';

    return (
        <div className={`alh-card-event${isOpen ? ' alh-card-event-open' : ''}`}>
            {/* card header row */}
            <div
                className="alh-card-event-row"
                role="button" tabIndex={0}
                onClick={onToggle}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onToggle(); }}
            >
                {/* left: time + sid */}
                <div className="alh-ce-left">
                    <div className="alh-ce-time">
                        <span className="alh-date">{fmtDate(event.occurred_at)}</span>
                        <span className="alh-time">{fmtTime(event.occurred_at)}</span>
                    </div>
                    <div className="alh-ce-sid">
                        <span className="alh-sid-text mono">{event.sid}</span>
                        <CopyButton text={event.sid} />
                    </div>
                </div>

                {/* right: pill + deltas + chevron */}
                <div className="alh-ce-right">
                    <span className={`alh-pill alh-pill-${event.change_type}`}>{event.change_type}</span>
                    <div className="alh-ce-deltas">
                        <span className="alh-ce-delta-item">
                            <span className="alh-ce-delta-lbl">Dest</span>
                            <span className={event.dest_count_delta > 0 ? 'pos' : event.dest_count_delta < 0 ? 'neg' : 'muted'}>
                                {fmtDelta(event.dest_count_delta)}
                            </span>
                        </span>
                        <span className="alh-ce-delta-item">
                            <span className="alh-ce-delta-lbl">Rng+</span>
                            <span className="pos">{event.ranges_added_count ?? '--'}</span>
                        </span>
                        <span className="alh-ce-delta-item">
                            <span className="alh-ce-delta-lbl">Rng−</span>
                            <span className="neg">{event.ranges_removed_count ?? '--'}</span>
                        </span>
                        <span className="alh-ce-delta-item">
                            <span className="alh-ce-delta-lbl">Rows</span>
                            <span className={event.total_rows_delta > 0 ? 'pos' : event.total_rows_delta < 0 ? 'neg' : 'muted'}>
                                {fmtDelta(event.total_rows_delta)}
                            </span>
                        </span>
                        <span className="alh-ce-delta-item">
                            <span className="alh-ce-delta-lbl">Lim</span>
                            <span className={(event.daily_limit_changed_count || event.hourly_limit_changed_count) ? 'pos' : 'muted'}>
                                {(event.daily_limit_changed_count || 0) + (event.hourly_limit_changed_count || 0)}
                            </span>
                        </span>
                    </div>
                    <span className="alh-ce-chevron" style={{ color: typeColor }}>
                        {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    </span>
                </div>
            </div>

            {/* expanded drawer — only destinations + count changes */}
            {isOpen && (
                <div className="alh-ce-drawer">
                    {/* Destinations Added */}
                    <div className="alh-dsection">
                        <div className="alh-dlabel">Destinations Added</div>
                        <div className="alh-dlist">
                            {destAdded.length === 0
                                ? <span className="alh-dempty">—</span>
                                : destAdded.map((item) => {
                                    const hasRng = rngAdded.some((r) => r.destination === item.destination);
                                    return (
                                        <span
                                            key={item.destination}
                                            className={`alh-chip alh-chip-pos${hasRng ? ' alh-chip-clickable' : ''}`}
                                            onClick={hasRng ? (e) => onDestClick(e, item.destination, rngAdded, 'added') : undefined}
                                            title={hasRng ? 'Click to view ranges' : undefined}
                                        >
                                            <span>{item.destination}</span>
                                            {hasRng && <List size={9} style={{ flexShrink: 0, opacity: 0.7 }} />}
                                            <CopyButton text={item.destination} size={9} />
                                        </span>
                                    );
                                })
                            }
                        </div>
                    </div>

                    {/* Destinations Removed */}
                    <div className="alh-dsection">
                        <div className="alh-dlabel">Destinations Removed</div>
                        <div className="alh-dlist">
                            {destRemoved.length === 0
                                ? <span className="alh-dempty">—</span>
                                : destRemoved.map((item) => {
                                    const hasRng = rngRemoved.some((r) => r.destination === item.destination);
                                    return (
                                        <span
                                            key={item.destination}
                                            className={`alh-chip alh-chip-neg${hasRng ? ' alh-chip-clickable' : ''}`}
                                            onClick={hasRng ? (e) => onDestClick(e, item.destination, rngRemoved, 'removed') : undefined}
                                            title={hasRng ? 'Click to view ranges' : undefined}
                                        >
                                            <span>{item.destination}</span>
                                            {hasRng && <List size={9} style={{ flexShrink: 0, opacity: 0.7 }} />}
                                            <CopyButton text={item.destination} size={9} />
                                        </span>
                                    );
                                })
                            }
                        </div>
                    </div>

                    {/* Count Changes */}
                    {destChanged.length > 0 && (
                        <div className="alh-dsection">
                            <div className="alh-dlabel">Count Changes</div>
                            <div className="alh-dlist">
                                {destChanged.map((item) => (
                                    <span key={`${item.destination}-delta`} className="alh-chip alh-chip-mono">
                                        <span className="mono">{item.destination}</span>
                                        <span className="alh-chip-arrow">{item.previousCount}→{item.currentCount}</span>
                                        <CopyButton text={item.destination} size={9} />
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    {limitChanges.length > 0 && (
                        <div className="alh-dsection">
                            <div className="alh-dlabel">Limit Changes</div>
                            <div className="alh-dlist">
                                {limitChanges.map((item, idx) => (
                                    <span key={`${item.destination}-limit-${idx}`} className="alh-chip alh-chip-mono alh-chip-limit">
                                        <span className="mono">{item.destination}</span>
                                        <span className="alh-chip-arrow">D {item.dailyLimitFrom || '--'}→{item.dailyLimitTo || '--'}</span>
                                        <span className="alh-chip-arrow">H {item.hourlyLimitFrom || '--'}→{item.hourlyLimitTo || '--'}</span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

/* ── Main ── */
const AccessListHistory = () => {
    const [loading, setLoading]       = useState(false);
    const [error, setError]           = useState('');
    const [rangeKey, setRangeKey]     = useState('7d');
    const [bucket, setBucket]         = useState('day');
    const [changeType, setChangeType] = useState('all');
    const [sidQuery, setSidQuery]     = useState('');
    const [events, setEvents]         = useState([]);
    const [stats, setStats]           = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [popup, setPopup]           = useState(null);
    const didAutoBackfill = useRef(false);

    const range = RANGE_OPTIONS.find((o) => o.value === rangeKey) || RANGE_OPTIONS[1];
    const { from, to } = useMemo(() => {
        const now = new Date();
        return { from: new Date(now.getTime() - range.ms).toISOString(), to: now.toISOString() };
    }, [range]);

    const queryParams = useMemo(() => {
        const p = new URLSearchParams();
        p.set('from', from); p.set('to', to); p.set('bucket', bucket);
        if (sidQuery.trim()) p.set('sid', sidQuery.trim());
        if (changeType !== 'all') p.set('changeType', changeType);
        return p.toString();
    }, [from, to, bucket, sidQuery, changeType]);

    const fetchHistory = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const initial = await fetchAccessListHistory(queryParams);
            const hj = initial.history;
            const sj = initial.stats;

            const shouldTryBackfill =
                !didAutoBackfill.current &&
                (hj.total || 0) === 0 &&
                !sidQuery.trim() &&
                changeType === 'all';

            if (shouldTryBackfill) {
                didAutoBackfill.current = true;
                await backfillAccessListHistory(true);
                const afterBackfill = await fetchAccessListHistory(queryParams);
                setEvents(afterBackfill.history.events || []);
                setStats(afterBackfill.stats);
                return;
            }

            setEvents(hj.events || []);
            setStats(sj);
        } catch (err) { setError(err.message); }
        finally { setLoading(false); }
    }, [queryParams, sidQuery, changeType]);

    useEffect(() => { fetchHistory(); }, [fetchHistory]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            fetchHistory();
        }, 30 * 60 * 1000);
        return () => clearInterval(intervalId);
    }, [fetchHistory]);

    const fmtDate  = (v) => { if (!v) return '--'; return new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }).replace(' ', '.'); };
    const fmtTime  = (v) => v ? new Date(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
    const fmtDelta = (n) => n == null ? '--' : n > 0 ? `+${n}` : `${n}`;

    const normalizeArray = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        if (typeof value === 'string') { try { const p = JSON.parse(value); return Array.isArray(p) ? p : []; } catch { return []; } }
        return [];
    };

    const openDestPopup = (e, destination, allRangeItems, kind) => {
        e.stopPropagation();
        const match = allRangeItems.find((r) => r.destination === destination);
        setPopup({ destination, ranges: match ? normalizeArray(match.ranges) : [], kind });
    };

    // Group events into pairs for 2-column layout
    const pairs = useMemo(() => {
        const out = [];
        for (let i = 0; i < events.length; i += 2) out.push(events.slice(i, i + 2));
        return out;
    }, [events]);

    const sharedProps = { normalizeArray, fmtDate, fmtTime, fmtDelta, onDestClick: openDestPopup };

    return (
        <div className="alh-root">
            <RangesPopup popup={popup} onClose={() => setPopup(null)} />

            {/* ── Header ── */}
            <div className="alh-header">
                <div className="alh-header-left">
                    <span className="alh-title">Access List History</span>
                    <span className="alh-dot" />
                    <span className="alh-sub">Track new, missing &amp; changed SIDs over time</span>
                </div>
                <div className="alh-header-right">
                    <div className="alh-range-badge">{fmtDate(from)} – {fmtDate(to)}</div>
                    <button className="alh-refresh" onClick={fetchHistory} disabled={loading}>
                        <RefreshCw size={13} style={loading ? { animation: 'alh-spin 1s linear infinite' } : {}} />
                        {loading ? 'Refreshing…' : 'Refresh'}
                    </button>
                </div>
            </div>

            {/* ── Filters ── */}
            <div className="alh-filters">
                <div className="alh-fchip">
                    <CalendarClock size={12} className="alh-ficon" />
                    <select value={rangeKey} onChange={(e) => setRangeKey(e.target.value)}>
                        {RANGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>
                <div className="alh-fchip">
                    <BarChart3 size={12} className="alh-ficon" />
                    <select value={bucket} onChange={(e) => setBucket(e.target.value)}>
                        {BUCKET_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>
                <div className="alh-fchip">
                    <Filter size={12} className="alh-ficon" />
                    <select value={changeType} onChange={(e) => setChangeType(e.target.value)}>
                        {CHANGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>
                <input
                    className="alh-search"
                    value={sidQuery}
                    onChange={(e) => setSidQuery(e.target.value)}
                    placeholder="Search SID…"
                />
            </div>

            <div className="alh-body">
                {error && <div className="alh-error">{error}</div>}

                {/* ── Summary cards ── */}
                <div className="alh-summary">
                    {[
                        { label: 'Total Events', value: stats?.summary?.total,         accent: '#64748b' },
                        { label: 'New',          value: stats?.summary?.new_count,     accent: '#10b981' },
                        { label: 'Missing',      value: stats?.summary?.missing_count, accent: '#f97316' },
                        { label: 'Changed',      value: stats?.summary?.changed_count, accent: '#38bdf8' },
                        {
                            label: 'Hot Limit SIDs (1h)',
                            value: stats?.frequentLimitChanges?.length || 0,
                            accent: '#ef4444',
                            pulse: (stats?.frequentLimitChanges?.length || 0) > 0
                        },
                    ].map(({ label, value, accent }) => (
                        <div key={label} className={`alh-card ${label === 'Hot Limit SIDs (1h)' && (stats?.frequentLimitChanges?.length || 0) > 0 ? 'alh-card-pulse' : ''}`} style={{ borderLeftColor: accent }}>
                            <div className="alh-card-label">{label}</div>
                            <div className="alh-card-value" style={{ color: accent }}>{value ?? '--'}</div>
                        </div>
                    ))}
                </div>

                {/* ── Insights ── */}
                <div className="alh-insights">
                    {[
                        { title: 'Most Active SIDs',   items: stats?.topSids           || [], valueFn: (i) => i.total },
                        { title: 'SID Flappers',       items: stats?.flappers          || [], valueFn: (i) => i.total },
                        { title: 'Destination Growth', items: stats?.destinationGrowth || [], valueFn: (i) => `+${i.destinations_added}` },
                    ].map(({ title, items, valueFn }) => (
                        <div key={title} className="alh-insight">
                            <div className="alh-insight-title">{title}</div>
                            {items.length === 0
                                ? <div className="alh-insight-empty">No data</div>
                                : items.map((item) => (
                                    <div key={item.sid} className="alh-insight-row">
                                        <span className="alh-insight-sid">
                                            <span className="mono">{item.sid}</span>
                                            <CopyButton text={item.sid} size={10} />
                                        </span>
                                        <span className="alh-insight-val">{valueFn(item)}</span>
                                    </div>
                                ))
                            }
                        </div>
                    ))}
                </div>

                {/* ── Change Events — 2-column card grid ── */}
                <div className="alh-table-wrap">
                    <div className="alh-table-header">
                        <span className="alh-table-title">Change Events</span>
                        <span className="alh-event-count">{events.length} event{events.length !== 1 ? 's' : ''}</span>
                    </div>

                    {events.length === 0 && !loading && (
                        <div className="alh-empty">
                            No events in this time range. History entries are created only when the access list actually changes.
                            Run <strong>Access List Changes</strong> refresh and check again.
                        </div>
                    )}

                    <div className="alh-events-container">
                        {pairs.map((pair, pairIdx) => {
                            const openEvent = pair.find((e) => e.id === expandedId) || null;
                            return (
                                <div key={pairIdx} className="alh-pair">
                                    {/* 2 cards side by side */}
                                    <div className="alh-pair-row">
                                        {pair.map((event) => (
                                            <EventCard
                                                key={event.id}
                                                event={event}
                                                isOpen={expandedId === event.id}
                                                onToggle={() => setExpandedId(expandedId === event.id ? null : event.id)}
                                                {...sharedProps}
                                            />
                                        ))}
                                        {/* spacer if odd pair */}
                                        {pair.length === 1 && <div className="alh-pair-spacer" />}
                                    </div>

                                    {/* full-width drawer when any card in this pair is open */}
                                    {openEvent && (() => {
                                        const destAdded   = normalizeArray(openEvent.destinations_added);
                                        const destRemoved = normalizeArray(openEvent.destinations_removed);
                                        const rngAdded    = normalizeArray(openEvent.ranges_added);
                                        const rngRemoved  = normalizeArray(openEvent.ranges_removed);
                                        const destChanged = normalizeArray(openEvent.destinations_changed);
                                        const limitChanges = normalizeArray(openEvent.limit_changes);
                                        return (
                                            <div className="alh-pair-drawer">
                                                <div className="alh-dsection">
                                                    <div className="alh-dlabel">Destinations Added</div>
                                                    <div className="alh-dlist">
                                                        {destAdded.length === 0
                                                            ? <span className="alh-dempty">—</span>
                                                            : destAdded.map((item) => {
                                                                const hasRng = rngAdded.some((r) => r.destination === item.destination);
                                                                return (
                                                                    <span
                                                                        key={item.destination}
                                                                        className={`alh-chip alh-chip-pos${hasRng ? ' alh-chip-clickable' : ''}`}
                                                                        onClick={hasRng ? (e) => openDestPopup(e, item.destination, rngAdded, 'added') : undefined}
                                                                        title={hasRng ? 'Click to view ranges' : undefined}
                                                                    >
                                                                        <span>{item.destination}</span>
                                                                        {hasRng && <List size={9} style={{ flexShrink: 0, opacity: 0.7 }} />}
                                                                        <CopyButton text={item.destination} size={9} />
                                                                    </span>
                                                                );
                                                            })
                                                        }
                                                    </div>
                                                </div>
                                                <div className="alh-dsection">
                                                    <div className="alh-dlabel">Destinations Removed</div>
                                                    <div className="alh-dlist">
                                                        {destRemoved.length === 0
                                                            ? <span className="alh-dempty">—</span>
                                                            : destRemoved.map((item) => {
                                                                const hasRng = rngRemoved.some((r) => r.destination === item.destination);
                                                                return (
                                                                    <span
                                                                        key={item.destination}
                                                                        className={`alh-chip alh-chip-neg${hasRng ? ' alh-chip-clickable' : ''}`}
                                                                        onClick={hasRng ? (e) => openDestPopup(e, item.destination, rngRemoved, 'removed') : undefined}
                                                                        title={hasRng ? 'Click to view ranges' : undefined}
                                                                    >
                                                                        <span>{item.destination}</span>
                                                                        {hasRng && <List size={9} style={{ flexShrink: 0, opacity: 0.7 }} />}
                                                                        <CopyButton text={item.destination} size={9} />
                                                                    </span>
                                                                );
                                                            })
                                                        }
                                                    </div>
                                                </div>
                                                {destChanged.length > 0 && (
                                                    <div className="alh-dsection">
                                                        <div className="alh-dlabel">Count Changes</div>
                                                        <div className="alh-dlist">
                                                            {destChanged.map((item) => (
                                                                <span key={`${item.destination}-delta`} className="alh-chip alh-chip-mono">
                                                                    <span className="mono">{item.destination}</span>
                                                                    <span className="alh-chip-arrow">{item.previousCount}→{item.currentCount}</span>
                                                                    <CopyButton text={item.destination} size={9} />
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {limitChanges.length > 0 && (
                                                    <div className="alh-dsection">
                                                        <div className="alh-dlabel">Limit Changes</div>
                                                        <div className="alh-dlist">
                                                            {limitChanges.map((item, idx) => (
                                                                <span key={`${item.destination}-limit-${idx}`} className="alh-chip alh-chip-mono alh-chip-limit">
                                                                    <span className="mono">{item.destination}</span>
                                                                    <span className="alh-chip-arrow">D {item.dailyLimitFrom || '--'}→{item.dailyLimitTo || '--'}</span>
                                                                    <span className="alh-chip-arrow">H {item.hourlyLimitFrom || '--'}→{item.hourlyLimitTo || '--'}</span>
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

                .alh-root { min-height: 100vh; background: #080d16; color: #cbd5e1; font-family: 'Inter', sans-serif; font-size: 13px; }
                .mono { font-family: 'JetBrains Mono', monospace; }

                /* Header */
                .alh-header { display: flex; align-items: center; justify-content: space-between; padding: 12px 20px; border-bottom: 1px solid #131c2e; background: #090e18; gap: 12px; flex-wrap: wrap; }
                .alh-header-left { display: flex; align-items: center; gap: 10px; }
                .alh-title { font-size: 15px; font-weight: 700; color: #f1f5f9; letter-spacing: -0.01em; }
                .alh-dot   { width: 4px; height: 4px; border-radius: 50%; background: #334155; }
                .alh-sub   { font-size: 11px; color: rgb(245,158,11); }
                .alh-header-right { display: flex; align-items: center; gap: 8px; }
                .alh-range-badge { font-size: 11px; font-family: 'JetBrains Mono', monospace; color: rgb(245,158,11); background: #0f172a; border: 1px solid #1e293b; border-radius: 4px; padding: 3px 8px; }
                .alh-refresh { display: inline-flex; align-items: center; gap: 5px; padding: 5px 12px; border-radius: 5px; border: 1px solid #16a34a; background: rgba(22,163,74,0.15); color: #22c55e; font-size: 12px; font-weight: 600; cursor: pointer; transition: background 0.15s; }
                .alh-refresh:hover    { background: rgba(22,163,74,0.25); }
                .alh-refresh:disabled { opacity: 0.5; cursor: not-allowed; }
                @keyframes alh-spin { to { transform: rotate(360deg); } }

                /* Filters */
                .alh-filters { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; padding: 8px 20px; background: #090e18; border-bottom: 1px solid #131c2e; }
                .alh-fchip { display: inline-flex; align-items: center; gap: 5px; padding: 4px 9px; background: #0d1626; border: 1px solid #1a2640; border-radius: 5px; color: #64748b; }
                .alh-fchip select { background: transparent; border: none; color: #94a3b8; font-size: 12px; outline: none; cursor: pointer; }
                .alh-ficon  { flex-shrink: 0; }
                .alh-search { padding: 4px 9px; border-radius: 5px; border: 1px solid #1a2640; background: #0d1626; color: #e2e8f0; font-size: 12px; outline: none; width: 140px; }
                .alh-search::placeholder { color: #334155; }
                .alh-search:focus { border-color: #334e6b; }

                /* Body */
                .alh-body  { padding: 16px 20px 32px; }
                .alh-error { background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.3); color: #f87171; padding: 8px 12px; border-radius: 6px; margin-bottom: 14px; font-size: 12px; }

                /* Summary */
                .alh-summary { display: grid; grid-template-columns: repeat(auto-fit,minmax(130px,1fr)); gap: 8px; margin-bottom: 12px; }
                .alh-card { background: #0c1422; border: 1px solid #182030; border-left: 3px solid; border-radius: 6px; padding: 10px 12px; }
                .alh-card-pulse { animation: alhHotPulse 1.8s ease-in-out infinite; }
                @keyframes alhHotPulse {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.12); }
                    50% { box-shadow: 0 0 0 8px rgba(239,68,68,0); }
                }
                .alh-card-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.07em; color: #475569; margin-bottom: 4px; }
                .alh-card-value { font-size: 22px; font-weight: 700; line-height: 1; font-variant-numeric: tabular-nums; }

                /* Insights */
                .alh-insights { display: grid; grid-template-columns: repeat(auto-fit,minmax(200px,1fr)); gap: 8px; margin-bottom: 14px; }
                .alh-insight { background: #0c1422; border: 1px solid #182030; border-radius: 6px; padding: 10px 12px; }
                .alh-insight-title { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; color: rgb(245,158,11); margin-bottom: 8px; }
                .alh-insight-empty { font-size: 11px; color: #334155; }
                .alh-insight-row { display: flex; justify-content: space-between; align-items: center; padding: 4px 0; border-bottom: 1px solid #131c2e; gap: 6px; }
                .alh-insight-row:last-child { border-bottom: none; }
                .alh-insight-sid { display: flex; align-items: center; gap: 4px; font-size: 11px; color: #94a3b8; min-width: 0; overflow: hidden; }
                .alh-insight-sid .mono { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12.2px; }
                .alh-insight-val { font-size: 11px; color: rgb(245,158,11); flex-shrink: 0; font-variant-numeric: tabular-nums; }

                /* Change Events outer wrap */
                .alh-table-wrap { background: #0c1422; border: 1px solid #182030; border-radius: 8px; overflow: hidden; }
                .alh-table-header { display: flex; align-items: center; justify-content: space-between; padding: 9px 14px; border-bottom: 1px solid #182030; background: #0a111f; }
                .alh-table-title { font-size: 12px; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.06em; }
                .alh-event-count { font-size: 11px; color: rgb(245,158,11); }
                .alh-empty { padding: 18px 14px; color: #334155; font-size: 12px; }

                /* 2-column grid container */
                .alh-events-container { padding: 8px; display: flex; flex-direction: column; gap: 4px; }

                /* each "row" of 2 events */
                .alh-pair { display: flex; flex-direction: column; gap: 0; }
                .alh-pair-row { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; }
                .alh-pair-spacer { } /* empty half */

                /* Event card */
                .alh-card-event {
                    background: #0a111f;
                    border: 1px solid #182030;
                    border-radius: 6px;
                    overflow: hidden;
                    transition: border-color 0.15s;
                }
                .alh-card-event:hover { border-color: #243044; }
                .alh-card-event-open  { border-color: rgba(56,189,248,0.3) !important; }

                .alh-card-event-row {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 8px 11px;
                    cursor: pointer;
                    outline: none;
                    gap: 8px;
                }
                .alh-card-event-row:hover { background: rgba(255,255,255,0.02); }
                .alh-card-event-row:focus-visible { box-shadow: inset 0 0 0 1px #2563eb; }

                .alh-ce-left { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
                .alh-ce-time { display: flex; align-items: baseline; gap: 5px; }
                .alh-date { font-size: 11px; color: #94a3b8; font-variant-numeric: tabular-nums; }
                .alh-time { font-size: 10px; color: #334155; font-variant-numeric: tabular-nums; }
                .alh-ce-sid { display: flex; align-items: center; gap: 4px; min-width: 0; }
                .alh-sid-text { font-size: 12px; font-weight: 600; color: #e2e8f0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-family: 'JetBrains Mono', monospace; }

                .alh-ce-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
                .alh-ce-deltas { display: flex; gap: 8px; }
                .alh-ce-delta-item { display: flex; flex-direction: column; align-items: center; gap: 1px; }
                .alh-ce-delta-lbl { font-size: 8px; text-transform: uppercase; letter-spacing: 0.06em; color: #ffffff; }
                .alh-ce-delta-item span:last-child { font-size: 11px; font-weight: 600; font-variant-numeric: tabular-nums; }
                .alh-ce-chevron { display: flex; align-items: center; }

                /* copy btn */
                .copy-btn { display: inline-flex; align-items: center; justify-content: center; background: none; border: none; cursor: pointer; color: #334155; padding: 2px; border-radius: 3px; flex-shrink: 0; transition: color 0.15s; line-height: 0; }
                .copy-btn:hover { color: #64748b; }

                /* pills */
                .alh-pill { display: inline-block; padding: 2px 7px; border-radius: 999px; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; white-space: nowrap; }
                .alh-pill-new     { background: rgba(16,185,129,0.15); color: #10b981; border: 1px solid rgba(16,185,129,0.25); }
                .alh-pill-missing { background: rgba(249,115,22,0.15); color: #f97316; border: 1px solid rgba(249,115,22,0.25); }
                .alh-pill-changed { background: rgba(56,189,248,0.15); color: #38bdf8; border: 1px solid rgba(56,189,248,0.25); }

                .pos   { color: #10b981; }
                .neg   { color: #f97316; }
                .muted { color: #475569; }

                /* full-width drawer below a pair */
                .alh-pair-drawer {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 14px;
                    padding: 10px 12px;
                    background: #07101c;
                    border: 1px solid rgba(56,189,248,0.15);
                    border-top: none;
                    border-radius: 0 0 6px 6px;
                    margin-top: -1px;
                }

                /* inline card drawer (kept for EventCard) */
                .alh-ce-drawer {
                    display: flex; flex-wrap: wrap; gap: 12px;
                    padding: 8px 11px 10px;
                    background: #060d18;
                    border-top: 1px solid #131c2e;
                }

                .alh-dsection { display: flex; flex-direction: column; gap: 5px; }
                .alh-dlabel { font-size: 9px; text-transform: uppercase; letter-spacing: 0.09em; color: #334155; font-weight: 600; }
                .alh-dlist  { display: flex; flex-wrap: wrap; gap: 4px; }
                .alh-dempty { font-size: 11px; color: #1e293b; }

                /* chips */
                .alh-chip { display: inline-flex; align-items: center; gap: 4px; padding: 2px 7px; border-radius: 4px; background: #0d1626; border: 1px solid #1a2640; font-size: 10px; color: #64748b; max-width: 220px; overflow: hidden; white-space: nowrap; }
                .alh-chip > span:first-child { overflow: hidden; text-overflow: ellipsis; }
                .alh-chip-pos { border-color: rgba(16,185,129,0.3); color: #10b981; }
                .alh-chip-neg { border-color: rgba(249,115,22,0.3); color: #f97316; }
                .alh-chip-mono { font-family: 'JetBrains Mono', monospace; font-size: 9.5px; }
                .alh-chip-limit { border-color: rgba(239,68,68,0.35); color: #fca5a5; }
                .alh-chip-clickable { cursor: pointer; transition: opacity 0.15s, box-shadow 0.15s; }
                .alh-chip-clickable:hover { opacity: 0.85; box-shadow: 0 0 0 1px currentColor; }
                .alh-chip-count { flex-shrink: 0; opacity: 0.6; font-size: 9px; }
                .alh-chip-arrow { flex-shrink: 0; opacity: 0.6; font-size: 9px; font-family: 'JetBrains Mono', monospace; }

                /* Ranges Popup */
                .rpm-overlay { position: fixed; inset: 0; z-index: 9999; background: rgba(0,0,0,0.65); backdrop-filter: blur(3px); display: flex; align-items: center; justify-content: center; padding: 20px; }
                .rpm-modal { background: #0c1422; border: 1px solid #1e2d45; border-radius: 10px; width: 100%; max-width: 480px; max-height: 70vh; display: flex; flex-direction: column; box-shadow: 0 24px 60px rgba(0,0,0,0.6); overflow: hidden; }
                .rpm-header { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; border-bottom: 1px solid; gap: 8px; flex-shrink: 0; }
                .rpm-header-left  { display: flex; align-items: center; gap: 8px; min-width: 0; }
                .rpm-dest { font-size: 12px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
                .rpm-badge { font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; padding: 2px 6px; border-radius: 999px; flex-shrink: 0; }
                .rpm-header-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
                .rpm-count { font-size: 11px; color: #475569; }
                .rpm-close { display: inline-flex; align-items: center; justify-content: center; background: none; border: none; cursor: pointer; color: #475569; padding: 3px; border-radius: 4px; line-height: 0; transition: color 0.15s; }
                .rpm-close:hover { color: #94a3b8; }
                .rpm-body { overflow-y: auto; padding: 8px 0; flex: 1; }
                .rpm-body::-webkit-scrollbar       { width: 4px; }
                .rpm-body::-webkit-scrollbar-track  { background: transparent; }
                .rpm-body::-webkit-scrollbar-thumb  { background: #1e2d45; border-radius: 2px; }
                .rpm-row { display: flex; align-items: center; gap: 10px; padding: 5px 14px; border-bottom: 1px solid #0f1a2a; transition: background 0.1s; }
                .rpm-row:last-child { border-bottom: none; }
                .rpm-row:hover { background: rgba(255,255,255,0.03); }
                .rpm-idx   { font-size: 9px; color: #1e2d45; width: 18px; text-align: right; flex-shrink: 0; font-variant-numeric: tabular-nums; }
                .rpm-range { font-family: 'JetBrains Mono', monospace; font-size: 11px; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
                .rpm-empty { padding: 18px 14px; font-size: 12px; color: #334155; }
                .rpm-footer { display: flex; align-items: center; gap: 6px; padding: 8px 14px; border-top: 1px solid #131c2e; background: #090e18; flex-shrink: 0; }
                .rpm-copy-label { font-size: 11px; color: #475569; }

                /* Responsive */
                @media (max-width: 700px) {
                    .alh-pair-row { grid-template-columns: 1fr; }
                    .alh-ce-deltas { display: none; }
                }
            `}</style>
        </div>
    );
};

export default AccessListHistory;
